import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';
import moment from 'moment';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';

import { IqTimeSeriesGroup } from '../../domain/iq-aggregated-dashboard.model';
import {
  buildUtcDateRange,
  capitalizeFirst,
} from '../../utils/iq-observations-chart.utils';

const SERIES_COLORS: Record<string, string> = {
  observe: '#61F29D',
  monitor: '#FFEB40',
  investigate: '#EF4732',
};

type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  grid: ApexGrid;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  colors: string[];
};

@Component({
  selector: 'app-iq-observations-chart',
  templateUrl: './iq-observations-chart.component.html',
  styleUrls: ['../../../../shared/analytics/analytics.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgApexchartsModule],
})
export class IqObservationsChartComponent {
  readonly data = input<IqTimeSeriesGroup[]>([]);

  readonly begin = input<string>('');

  readonly end = input<string>('');

  readonly chartOptions = computed<Partial<ChartOptions>>(() => {
    const groups = this.data() ?? [];
    const categories = buildUtcDateRange(this.begin(), this.end());

    const series: ApexAxisChartSeries = groups.map((group) => {
      const lookup = new Map(group.dataPoints.map((p) => [p.date, p.count]));
      return {
        name: capitalizeFirst(group.actionLabel),
        data: categories.map((date) => lookup.get(date) ?? 0),
      };
    });

    const colors = groups.map(
      (g) => SERIES_COLORS[g.actionLabel.toLowerCase()] ?? '#2B2B2B',
    );

    return {
      series,
      colors,
      chart: {
        type: 'line',
        height: 320,
        toolbar: { show: true, tools: { download: false } },
        zoom: { enabled: false },
      },
      stroke: { curve: 'smooth', width: 3 },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          formatter: (val: string) => moment.utc(val).format('MM/DD'),
        },
      },
      yaxis: {
        title: { text: 'Count' },
        min: 0,
        forceNiceScale: true,
      },
      grid: { borderColor: '#D6D6D6' },
      dataLabels: { enabled: false },
      tooltip: {
        x: {
          formatter: (val: number) => {
            const date = categories[val - 1];
            return date ? moment.utc(date).format('MMM DD, YYYY') : '';
          },
        },
      },
      legend: { position: 'bottom' },
    };
  });
}
